import _ from "lodash";
import { createTheme } from "@mui/material/styles";
import { typography } from "./Typography";
import { components } from "./Override";
import { shadows } from "./Shadows";
import { useSelector } from "react-redux";

// ##############################import {
import {
  BLUE_THEME,
  GREEN_THEME,
  RED_THEME,
  BLACK_THEME,
  PURPLE_THEME,
  INDIGO_THEME,
  ORANGE_THEME,
  LIGHT_BLUE_THEME,
  MAROON_THEME,
  FEE_MANAGEMENT_THEME,
} from "../../redux/constants";
import { IStoreState } from "../../redux/initialStoreState";
import { palette, Theme } from "@mui/system";
// // // Global Variables
// ##############################

const SidebarWidth = 265;
const SidebarMobileWidth = 250;
const TopbarHeight = 60;

const baseTheme = {
  direction: "ltr",
  palette: {
    primary: {
      main: "#1a97f5",
      light: "#cce6fc",      // was #e6f4ff — darker tint, still blue but more visible
      dark: "#1682d4",
    },
    secondary: {
      main: "#1e4db7",
      light: "#b8ceff",      // was #ddebff — richer indigo tint
      dark: "#173f98",
    },

    success: {
      main: "#027302",
      light: "#c3edd0",      // was #ebfaf2 — more saturated green tint
      dark: "#025902",
      contrastText: "#ffffff",
    },
    danger: {
      main: "#e46a76",
      light: "#fad8db",      // was #fdf3f5
    },
    info: {
      main: "#0bb2fb",
      light: "#a7e3f4",
    },
    error: {
      main: "#b4314a",       // brand red
      light: "#f7e6ea",      // soft tint
      dark: "#95293d",       // hover/pressed
    },
    warning: {
      main: "#fec90f",
      light: "#fde8a0",      // was #fff4e5 — more visible amber tint
      dark: "#dcb014",
      contrastText: "#ffffff",
    },
    black: {
      main: "#1c1c1c",
      light: "#3a3a3a",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    text: {
      secondary: "#505764",  // was #777e89 — ~30% darker for better readability
      danger: "#fc4b6c",
    },
    grey: {
      A100: "#dde3e8",       // was #ecf0f2 — darker neutral surface
      A200: "#6b7785",       // was #99abb4 — darker muted text/icons
      A400: "#565e6a",       // was #767e89 — slightly darker
      A700: "#cce6fc",       // was #e6f4ff — matches new primary.light
    },
    action: {
      disabledBackground: "rgba(73,82,88,0.12)",
      hoverOpacity: 0.04,    // was 0.02 — slightly more visible hover
      hover: "rgba(0, 0, 0, 0.05)",  // was 0.03
    },
  },

  shape: {
    borderRadius: 5,
  },
  mixins: {
    toolbar: {
      color: "#949db2",
      "@media(min-width:1280px)": {
        minHeight: TopbarHeight,
        padding: "0 30px",
      },
      "@media(max-width:1280px)": {
        minHeight: TopbarHeight,
      },
    },
  },
  status: {
    danger: "#e53e3e",
  },
  components,
  typography,
  shadows,
};

const themesOptions = [
  {
    name: FEE_MANAGEMENT_THEME,
    palette: {
      primary: {
        main: "#0D3B66",
        light: "#e6f4ff",
        dark: "#EE964B",
      },
      secondary: {
        main: "#1e4db7",
        light: "#ddebff",
        dark: "#173f98",
      },
    },
  },
  {
    name: BLUE_THEME,
    palette: {
      primary: {
        main: "#1a97f5",
        light: "#cce6fc",    // was #e6f4ff
        dark: "#1682d4",
      },
      secondary: {
        main: "#1e4db7",
        light: "#b8ceff",    // was #ddebff
        dark: "#173f98",
      },
    },
  },
  {
    name: LIGHT_BLUE_THEME,
    palette: {
      primary: {
        main: "rgb(24, 144, 255)",
        light: "rgb(186, 224, 255)",  // was rgb(230, 247, 255)
        dark: "rgb(24, 144, 255)",
      },
      secondary: {
        main: "#1e4db7",
        light: "#b8ceff",    // was #ddebff
        dark: "#173f98",
      },
    },
  },
  {
    name: GREEN_THEME,
    palette: {
      primary: {
        main: "#00cec3",
        light: "#a8ecea",    // was #d7f8f6
        dark: "#02b3a9",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#066a73",
      },
    },
  },
  {
    name: PURPLE_THEME,
    palette: {
      primary: {
        main: "#7352ff",
        light: "#c5baf7",    // was #e5e0fa
        dark: "#5739d6",
      },
      secondary: {
        main: "#402e8d",
      },
    },
  },
  {
    name: INDIGO_THEME,
    palette: {
      primary: {
        main: "#004aad",
        light: "#b3ccf0",    // was #e6f4ff
        dark: "#0c399e",
      },
      secondary: {
        main: "#11397b",
      },
    },
  },
  {
    name: ORANGE_THEME,
    palette: {
      primary: {
        main: "#03c9d7",
        light: "#e5fafb",
        dark: "#05b2bd",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#fb9678",
        light: "#fcf1ed",
        dark: "#e67e5f",
        contrastText: "#ffffff",
      },
    },
  },
  {
    name: RED_THEME,
    palette: {
      primary: {
        main: "#ff5c8e",
        light: "#fce6ed",
        dark: "#d43653",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#5e244d",
      },
    },
  },
  {
    name: BLACK_THEME,
    palette: {
      primary: {
        main: "#1c2025",
      },
    },
  },
  {
    name: MAROON_THEME,
    palette: {
      primary: {
        main: "#800000",
        light: "#510400",
        dark: "#510400",
      },
      secondary: {
        main: "#510400",
      },
    },
  },
];

export const BuildTheme = (config: any = {}) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);
  const customizer = useSelector(
    (state: IStoreState) => state.customizerReducer
  );

  const baseMode = {
    palette: {
      mode: customizer.activeMode,
      background: {
        default: customizer.activeMode === "dark" ? "#20232a" : "#eee",  // was #fafbfb #f0f4f8— more contrast
        dark: customizer.activeMode === "dark" ? "#1c2025" : "#ffffff",
        paper: customizer.activeMode === "dark" ? "#282C34" : "#ffffff",
      },
      text: {
        primary:
          customizer.activeMode === "dark" ? "#e6e5e8" : "#1a1f2b",  // was rgba(0,0,0,0.87) — richer dark
        secondary: customizer.activeMode === "dark" ? "#adb0bb" : "#505764",  // was #777e89
      },
    },
  };
  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  const theme = createTheme(
    //@ts-ignore
    _.merge({}, baseTheme, baseMode, themeOptions, {
      direction: config.direction,
      border: "rgb(240, 240, 240)",
    })
  );
  return theme;
};

export { TopbarHeight, SidebarWidth, SidebarMobileWidth, baseTheme };
